/**
 * inline-edit.ts - Inline editing for tournament attributes
 */

import { updateTournament as updateTournamentAPI } from '../api/tournament-api';
import { t } from '../utils/i18n-helper';
import { escapeHtml } from '../utils/security';
import { toggleLocks } from './publication';

/**
 * Toggle tournament details section visibility
 */
export function toggleTournamentDetails(tournamentId: string): void {
  const details = document.getElementById(`tournament-details-${tournamentId}`);
  const button = (event?.target as HTMLElement)?.closest('.expand-toggle-btn') as HTMLElement;

  if (!details) return;

  const isHidden = details.style.display === 'none';
  details.style.display = isHidden ? 'block' : 'none';

  if (button) {
    if (isHidden) {
      button.classList.add('expanded');
    } else {
      button.classList.remove('expanded');
    }
  }
}

/**
 * Edit a field inline
 */
export async function editField(
  tournamentId: string,
  fieldName: string,
  currentValue: string,
  fieldType: 'text' | 'select' | 'checkbox' | 'date',
  options?: string[],
  autoSave: boolean = false
): Promise<void> {
  const field = event?.target as HTMLElement;
  if (!field) return;

  // Stop event propagation to prevent parent handlers (like section toggle) from firing
  if (event) {
    event.stopPropagation();
  }

  // Support .editable-field, .detail-value, .info-row-editable, and .editable-title patterns
  let fieldContainer = field.closest('.editable-field') as HTMLElement;
  let fieldValueSpan: HTMLElement | null = null;
  let isDetailValuePattern = false;
  let isTitlePattern = false;

  if (fieldContainer) {
    fieldValueSpan = fieldContainer.querySelector('.field-value') as HTMLElement;
  } else {
    // Try editable-title pattern (used for tournament name in header)
    fieldContainer = field.closest('.editable-title') as HTMLElement;
    if (fieldContainer) {
      // For title pattern, the container itself is the editable element
      // We'll treat the first text node as the value
      fieldValueSpan = fieldContainer;
      isTitlePattern = true;
      isDetailValuePattern = true; // Use similar editing logic
    } else {
      // Try detail-value pattern
      fieldContainer = field.closest('.detail-value') as HTMLElement;
      if (fieldContainer) {
        // For detail-value pattern, find the first span child (the value display)
        fieldValueSpan = fieldContainer.querySelector('span:first-child') as HTMLElement;
        isDetailValuePattern = true;
      } else {
        // Try info-row-editable pattern (used for venue/location)
        fieldContainer = field.closest('.info-row-editable') as HTMLElement;
        if (fieldContainer) {
          fieldValueSpan = fieldContainer.querySelector('.info-text') as HTMLElement;
          isDetailValuePattern = true;
        }
      }
    }
  }

  if (!fieldContainer || !fieldValueSpan) return;

  // Prevent multiple edits
  if (fieldContainer.classList.contains('editing')) return;
  fieldContainer.classList.add('editing');

  // Save the original HTML - for detail-value pattern, save the entire container
  const originalHTML = isDetailValuePattern ? fieldContainer.innerHTML : fieldValueSpan.innerHTML;

  // Get the original text - for title pattern, extract just the text without the edit icon
  let originalText: string;
  if (isTitlePattern) {
    // For title, get the first text node's content (before the edit icon)
    const textNode = Array.from(fieldValueSpan.childNodes).find(node => node.nodeType === Node.TEXT_NODE);
    originalText = textNode ? (textNode.textContent || '').trim() : (fieldValueSpan.textContent || '').trim();
  } else {
    originalText = fieldValueSpan.textContent || '';
  }

  // For detail-value pattern, save and remove the onclick attribute to prevent conflicts
  // Also remove the 'editable' class which has pointer-events: none !important on children
  let savedOnClick: string | null = null;
  let hadEditableClass = false;
  if (isDetailValuePattern) {
    if (fieldContainer.hasAttribute('onclick')) {
      savedOnClick = fieldContainer.getAttribute('onclick');
      fieldContainer.removeAttribute('onclick');
    }
    if (fieldContainer.classList.contains('editable')) {
      hadEditableClass = true;
      fieldContainer.classList.remove('editable');
    }
  }

  // Create input based on type
  let inputHTML = '';
  const isLocationField = fieldName === 'venue';

  if (fieldType === 'text') {
    inputHTML = `
      <div style="position: relative; width: 100%;">
        <input type="text"
          class="inline-input"
          value="${currentValue}"
          placeholder="${isLocationField ? t('tournament.enterLocation', 'Enter location...') : t('common.enterValue', 'Enter value...')}"
          ${isLocationField ? 'autocomplete="off"' : ''}>
        ${isLocationField ? '<div class="location-suggestions" style="display: none; position: absolute; top: 100%; left: 0; right: 0; background: white; border: 2px solid #e5e7eb; border-top: none; border-radius: 0 0 8px 8px; max-height: 200px; overflow-y: auto; z-index: 1000; box-shadow: 0 4px 6px rgba(0,0,0,0.1);"></div>' : ''}
      </div>
    `;
  } else if (fieldType === 'date') {
    inputHTML = `
      <input type="date"
        class="inline-input"
        value="${currentValue}">
    `;
  } else if (fieldType === 'select' && options) {
    const optionsHTML = options.map(opt => {
      let label: string;
      if (opt === '') {
        label = t('common.notSet', 'Not set');
      } else if (fieldName === 'initialColor') {
        // Use translation for initialColor values
        label = t(`tournament.initialColor.${opt}`, opt.charAt(0).toUpperCase() + opt.slice(1));
      } else {
        label = opt.charAt(0).toUpperCase() + opt.slice(1);
      }
      const selected = opt === currentValue ? 'selected' : '';
      return `<option value="${opt}" ${selected}>${label}</option>`;
    }).join('');

    inputHTML = `
      <select class="inline-input">
        ${optionsHTML}
      </select>
    `;
  } else if (fieldType === 'checkbox') {
    const checked = currentValue === 'true';
    inputHTML = `
      <label class="toggle-switch-container">
        <input type="checkbox" class="toggle-switch-input inline-input" ${checked ? 'checked' : ''}>
        <span class="toggle-switch-slider"></span>
        <span class="toggle-switch-label">${t('common.enabled', 'Enabled')}</span>
      </label>
    `;
  }

  const buttonsHTML = autoSave ? '' : `
    <div style="display: flex; gap: 8px; margin-top: 8px; pointer-events: auto; position: relative; z-index: 1000;">
      <button class="btn-save-inline btn btn-primary btn-small" style="pointer-events: auto; cursor: pointer;">${t('common.save', 'Save')}</button>
      <button class="btn-cancel-inline btn btn-secondary btn-small" style="pointer-events: auto; cursor: pointer;">${t('common.cancel', 'Cancel')}</button>
    </div>
  `;

  // For detail-value pattern, we need to replace entire container content
  // For editable-field pattern, we only replace the field value span
  if (isDetailValuePattern) {
    fieldContainer.innerHTML = inputHTML + buttonsHTML;
    // Ensure the container itself doesn't block clicks
    fieldContainer.style.pointerEvents = 'auto';
    fieldContainer.style.position = 'relative';
    fieldContainer.style.zIndex = '998';
  } else {
    fieldValueSpan.innerHTML = inputHTML + buttonsHTML;
  }

  const input = fieldContainer.querySelector('.inline-input') as HTMLInputElement | HTMLSelectElement;

  if (!input) {
    if (isDetailValuePattern) {
      fieldContainer.innerHTML = originalHTML;
      if (savedOnClick) {
        fieldContainer.setAttribute('onclick', savedOnClick);
      }
      if (hadEditableClass) {
        fieldContainer.classList.add('editable');
      }
    } else {
      fieldValueSpan.innerHTML = originalHTML;
    }
    fieldContainer.classList.remove('editing');
    return;
  }

  const saveBtn = autoSave ? null : fieldContainer.querySelector('.btn-save-inline') as HTMLButtonElement;
  const cancelBtn = autoSave ? null : fieldContainer.querySelector('.btn-cancel-inline') as HTMLButtonElement;

  if (!autoSave && (!saveBtn || !cancelBtn)) {
    if (isDetailValuePattern) {
      fieldContainer.innerHTML = originalHTML;
      if (savedOnClick) {
        fieldContainer.setAttribute('onclick', savedOnClick);
      }
      if (hadEditableClass) {
        fieldContainer.classList.add('editable');
      }
    } else {
      fieldValueSpan.innerHTML = originalHTML;
    }
    fieldContainer.classList.remove('editing');
    return;
  }

  // Cancel handler
  let cancel = () => {
    if (isDetailValuePattern) {
      fieldContainer.innerHTML = originalHTML;
      // Restore the original onclick attribute if it was saved
      if (savedOnClick) {
        fieldContainer.setAttribute('onclick', savedOnClick);
      }
      // Restore the editable class if it was removed
      if (hadEditableClass) {
        fieldContainer.classList.add('editable');
      }
    } else {
      fieldValueSpan.innerHTML = originalHTML;
    }
    fieldContainer.classList.remove('editing');

    // Set a temporary lock to prevent the section from toggling immediately after cancel
    const lockKey = `${tournamentId}-details`;
    toggleLocks.set(lockKey, Date.now() + 300);
  };

  if (cancelBtn) {
    cancelBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      cancel();
    });
  }

  // Focus input
  if (input instanceof HTMLInputElement && (fieldType === 'text' || fieldType === 'date')) {
    input.focus();
    if (fieldType === 'text') {
      input.select();
    }

    // Setup location autocomplete if this is a location field
    if (isLocationField && fieldType === 'text') {
      const suggestionsDiv = fieldContainer.querySelector('.location-suggestions') as HTMLElement;
      let debounceTimer: number | undefined;

      const searchLocation = async (query: string) => {
        if (query.length < 2) {
          if (suggestionsDiv) suggestionsDiv.style.display = 'none';
          return;
        }

        try {
          const response = await fetch(`/api/tournaments/location-search?q=${encodeURIComponent(query)}&limit=5`);
          const data = await response.json();

          if (data.results && data.results.length > 0 && suggestionsDiv) {
            suggestionsDiv.innerHTML = data.results.map((result: any) => `
              <div class="location-suggestion-item" style="padding: 8px 12px; cursor: pointer; border-bottom: 1px solid #eee; transition: background-color 0.15s;"
                onmouseover="this.style.backgroundColor='#f5f5f5'"
                onmouseout="this.style.backgroundColor='white'"
                data-location="${escapeHtml(result.shortDisplayName || result.displayName)}"
                data-full-location="${escapeHtml(result.displayName)}">
                ${escapeHtml(result.shortDisplayName || result.displayName)}
              </div>
            `).join('');
            suggestionsDiv.style.display = 'block';

            // Add click handlers to suggestions
            suggestionsDiv.querySelectorAll('.location-suggestion-item').forEach(item => {
              item.addEventListener('click', () => {
                const location = (item as HTMLElement).getAttribute('data-location');
                if (location && input instanceof HTMLInputElement) {
                  input.value = location;
                  suggestionsDiv.style.display = 'none';
                }
              });
            });
          } else if (suggestionsDiv) {
            suggestionsDiv.style.display = 'none';
          }
        } catch (error) {
          console.error('Location search error:', error);
        }
      };

      input.addEventListener('input', () => {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(() => {
          searchLocation(input.value);
        }, 300);
      });

      // Hide suggestions when clicking outside
      const hideOnClickOutside = (e: MouseEvent) => {
        if (!fieldContainer.contains(e.target as Node) && suggestionsDiv) {
          suggestionsDiv.style.display = 'none';
        }
      };
      document.addEventListener('click', hideOnClickOutside);

      // Wrap cancel to cleanup event listener
      const originalCancel = cancel;
      cancel = () => {
        document.removeEventListener('click', hideOnClickOutside);
        originalCancel();
      };
    }
  } else if (input instanceof HTMLSelectElement) {
    // For select elements, focus and optionally open
    setTimeout(() => {
      input.focus();
    }, 10);
  }

  // Save handler
  const save = async () => {
    let newValue: any;

    if (fieldType === 'checkbox') {
      newValue = (input as HTMLInputElement).checked;
    } else if (fieldType === 'select') {
      newValue = (input as HTMLSelectElement).value || null;
    } else {
      newValue = (input as HTMLInputElement).value.trim() || null;

      // Convert numberOfRounds to a number
      if (fieldName === 'numberOfRounds' && newValue !== null) {
        const numValue = parseInt(newValue, 10);
        if (isNaN(numValue) || numValue < 1) {
          alert(t('tournament.validRounds', 'Please enter a valid number of rounds (minimum 1)'));
          return;
        }

        // Check if the new value is less than the current round
        const currentRound = parseInt(fieldContainer.getAttribute('data-current-round') || '0', 10);
        if (numValue < currentRound) {
          alert(t('tournament.roundsCannotBeLessThanCurrent', `Number of rounds cannot be less than the current round (${currentRound})`));
          return;
        }

        newValue = numValue;
      }
    }

    // Disable buttons during save (if they exist)
    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.textContent = t('common.saving', 'Saving...');
    }
    if (cancelBtn) {
      cancelBtn.disabled = true;
    }

    try {
      // Update via API
      const updates: any = {};
      updates[fieldName] = newValue;

      // If this is a venue field and has a value, geocode it to get coordinates
      if (fieldName === 'venue' && newValue) {
        try {
          const geoResponse = await fetch(`/api/tournaments/location-search?q=${encodeURIComponent(newValue)}&limit=1`);
          const geoData = await geoResponse.json();

          if (geoData.results && geoData.results.length > 0) {
            // Fetch full coordinates from geocoding service
            const coordResponse = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(newValue)}&format=json&limit=1`, {
              headers: {
                'User-Agent': 'NextRound.pro Chess Tournament Manager'
              }
            });
            const coordData = await coordResponse.json();

            if (coordData.length > 0) {
              updates.latitude = parseFloat(coordData[0].lat);
              updates.longitude = parseFloat(coordData[0].lon);
            }
          }
        } catch (geoError) {
          console.warn('Failed to geocode location, saving without coordinates:', geoError);
        }
      }

      await updateTournamentAPI(tournamentId, updates);

      // For venue field with coordinates, reload to show the map
      // For numberOfRounds field, reload to show the Edit Round Dates button
      // For date fields, reload to update the display in the quick info section
      if ((fieldName === 'venue' && (updates.latitude || updates.longitude)) || fieldName === 'numberOfRounds' || fieldName === 'startDate' || fieldName === 'endDate') {
        if (window.loadSingleTournament) {
          await window.loadSingleTournament(tournamentId);
        }
        return;
      }

      // Update the displayed value without reloading the entire tournament
      let displayValue: string;

      if (fieldType === 'checkbox') {
        displayValue = newValue ? '✓ ' + t('common.yes', 'Yes') : '✗ ' + t('common.no', 'No');
      } else if (fieldType === 'select' && fieldName === 'initialColor') {
        displayValue = t(`tournament.initialColor.${newValue}`, newValue.charAt(0).toUpperCase() + newValue.slice(1));
      } else if (fieldType === 'select') {
        displayValue = newValue ? newValue.charAt(0).toUpperCase() + newValue.slice(1) : '';
      } else {
        displayValue = newValue || '';
      }

      // Restore the original structure with the new value
      let newHTML: string;
      if (isTitlePattern) {
        // For title pattern, include the text and edit icon
        newHTML = `${escapeHtml(displayValue)}<span style="font-size: 0.7em; opacity: 0.5; margin-left: 4px;">✎</span>`;
      } else if (isDetailValuePattern) {
        newHTML = `<span>${escapeHtml(displayValue)}</span><span class="edit-icon-small">✎</span>`;
      } else {
        newHTML = displayValue;
      }

      if (isDetailValuePattern) {
        fieldContainer.innerHTML = newHTML;
        // Restore the onclick attribute
        if (savedOnClick) {
          fieldContainer.setAttribute('onclick', savedOnClick);
        }
        // Restore the editable class
        if (hadEditableClass) {
          fieldContainer.classList.add('editable');
        }
      } else {
        fieldValueSpan.innerHTML = newHTML;
      }

      fieldContainer.classList.remove('editing');

      // Set a temporary lock to prevent the section from toggling immediately after edit
      const lockKey = `${tournamentId}-details`;
      toggleLocks.set(lockKey, Date.now() + 300);
    } catch (error) {
      console.error('Failed to update field:', error);
      alert(t('common.error', 'Error') + ': ' + (error as Error).message);
      cancel();
    }
  };

  if (saveBtn) {
    saveBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      save();
    });
  }

  // Auto-save mode: save on blur for text/date, on change for select/checkbox
  if (autoSave) {
    if (fieldType === 'select' || fieldType === 'checkbox') {
      // Save immediately on change
      input.addEventListener('change', () => {
        // Set lock immediately to prevent section toggle while saving
        const lockKey = `${tournamentId}-details`;
        toggleLocks.set(lockKey, Date.now() + 500);
        save();
      });
    } else if (fieldType === 'text' || fieldType === 'date') {
      // Save on blur (when user clicks away or tabs out)
      input.addEventListener('blur', () => {
        // Set lock immediately to prevent section toggle while saving
        const lockKey = `${tournamentId}-details`;
        toggleLocks.set(lockKey, Date.now() + 500);

        // Small delay to allow click events to fire first
        setTimeout(() => {
          save();
        }, 100);
      });
    }
  }

  // Enter key to save (for text and date inputs)
  if (input instanceof HTMLInputElement && (fieldType === 'text' || fieldType === 'date')) {
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        save();
      } else if (e.key === 'Escape') {
        cancel();
      }
    });
  }
}

/**
 * Toggle tournament rated status
 */
export async function toggleRated(tournamentId: string, evt?: Event): Promise<void> {
  // Stop event propagation
  if (evt) {
    evt.stopPropagation();
  }

  // Get the toggle switch element and read current state from it
  const toggleSwitch = document.querySelector(`#rated-toggle-${tournamentId}`) as HTMLElement;
  if (!toggleSwitch) return;

  // Read current state from the 'active' class
  const currentValue = toggleSwitch.classList.contains('active');
  const newValue = !currentValue;

  try {
    await updateTournamentAPI(tournamentId, { isRated: newValue });

    // Update the toggle switch UI
    if (newValue) {
      toggleSwitch.classList.add('active');
    } else {
      toggleSwitch.classList.remove('active');
    }
  } catch (error) {
    console.error('Failed to update rated status:', error);
    alert(t('error.updateFailed', 'Failed to update. Please try again.'));
  }
}

/**
 * Set initial color value (for segmented control)
 */
export async function setInitialColor(tournamentId: string, value: string, evt?: Event): Promise<void> {
  // Stop event propagation
  if (evt) {
    evt.stopPropagation();
    evt.preventDefault();
  }

  // Check if the button is disabled (initial color locked after rounds generated)
  const target = evt?.target as HTMLElement;
  const button = target?.closest('.segment-button') as HTMLButtonElement;
  if (button?.disabled || button?.hasAttribute('disabled')) {
    alert(t('tournament.initialColorLockedAfterRounds', 'Initial color cannot be changed after rounds have been generated'));
    return;
  }

  try {
    await updateTournamentAPI(tournamentId, { initialColor: value });

    // Update the segmented control UI
    const buttons = document.querySelectorAll(`#initial-color-control-${tournamentId} .segment-button`);
    buttons.forEach(btn => {
      if (btn.getAttribute('data-value') === value) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
  } catch (error) {
    console.error('Failed to update initial color:', error);
    alert(t('error.updateFailed', 'Failed to update. Please try again.'));
  }
}

/**
 * Set time control value (for segmented control)
 */
export async function setTimeControl(tournamentId: string, value: string, evt?: Event): Promise<void> {
  // Stop event propagation
  if (evt) {
    evt.stopPropagation();
  }

  try {
    await updateTournamentAPI(tournamentId, { timeControl: value });

    // Update the segmented control UI
    const buttons = document.querySelectorAll(`#time-control-${tournamentId} .segment-button`);
    buttons.forEach(btn => {
      if (btn.getAttribute('data-value') === value) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
  } catch (error) {
    console.error('Failed to update time control:', error);
    alert(t('error.updateFailed', 'Failed to update. Please try again.'));
  }
}

/**
 * Set tournament format (for segmented control)
 */
export async function setFormat(tournamentId: string, value: string, evt?: Event): Promise<void> {
  // Stop event propagation
  if (evt) {
    evt.stopPropagation();
    evt.preventDefault();
  }

  // Check if the button is disabled (format locked after rounds generated)
  const target = evt?.target as HTMLElement;
  const button = target?.closest('.segment-button') as HTMLButtonElement;
  if (button?.disabled || button?.hasAttribute('disabled')) {
    alert(t('tournament.formatLockedAfterRounds', 'Format cannot be changed after rounds have been generated'));
    return;
  }

  try {
    await updateTournamentAPI(tournamentId, { format: value });

    // Update the segmented control UI
    const buttons = document.querySelectorAll(`#format-control-${tournamentId} .segment-button`);
    buttons.forEach(btn => {
      if (btn.getAttribute('data-value') === value) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });

    // Reload tournament to reflect format-specific UI changes (e.g., round settings)
    if (window.loadSingleTournament) {
      await window.loadSingleTournament(tournamentId);
    }
  } catch (error) {
    console.error('Failed to update format:', error);
    alert(t('error.updateFailed', 'Failed to update. Please try again.'));
  }
}

/**
 * Edit date range (start and end dates together)
 */
export async function editDateRange(tournamentId: string, startDate: string, endDate: string): Promise<void> {
  const field = event?.target as HTMLElement;
  if (!field) return;

  // Stop event propagation
  if (event) {
    event.stopPropagation();
  }

  const fieldContainer = field.closest('.detail-value') as HTMLElement;
  if (!fieldContainer) return;

  // Prevent multiple edits
  if (fieldContainer.classList.contains('editing')) return;
  fieldContainer.classList.add('editing');

  // Save original HTML
  const originalHTML = fieldContainer.innerHTML;
  let savedOnClick: string | null = null;
  let hadEditableClass = false;

  if (fieldContainer.hasAttribute('onclick')) {
    savedOnClick = fieldContainer.getAttribute('onclick');
    fieldContainer.removeAttribute('onclick');
  }
  if (fieldContainer.classList.contains('editable')) {
    hadEditableClass = true;
    fieldContainer.classList.remove('editable');
  }

  // Create date range inputs
  const inputHTML = `
    <div style="display: flex; flex-direction: column; gap: 8px; width: 100%;">
      <div style="display: flex; gap: 8px; align-items: center;">
        <label style="font-size: 12px; color: #6b7280; min-width: 60px;">${t('tournament.startDate', 'Start')}:</label>
        <input type="date" id="date-start-${tournamentId}" class="inline-input" value="${startDate}" style="flex: 1;">
      </div>
      <div style="display: flex; gap: 8px; align-items: center;">
        <label style="font-size: 12px; color: #6b7280; min-width: 60px;">${t('tournament.endDate', 'End')}:</label>
        <input type="date" id="date-end-${tournamentId}" class="inline-input" value="${endDate}" style="flex: 1;">
      </div>
    </div>
    <div style="display: flex; gap: 8px; margin-top: 12px;">
      <button class="btn-save-dates btn btn-primary btn-small">${t('common.save', 'Save')}</button>
      <button class="btn-cancel-dates btn btn-secondary btn-small">${t('common.cancel', 'Cancel')}</button>
    </div>
  `;

  fieldContainer.innerHTML = inputHTML;
  fieldContainer.style.pointerEvents = 'auto';

  const startInput = document.getElementById(`date-start-${tournamentId}`) as HTMLInputElement;
  const endInput = document.getElementById(`date-end-${tournamentId}`) as HTMLInputElement;
  const saveBtn = fieldContainer.querySelector('.btn-save-dates') as HTMLButtonElement;
  const cancelBtn = fieldContainer.querySelector('.btn-cancel-dates') as HTMLButtonElement;

  if (!startInput || !endInput || !saveBtn || !cancelBtn) {
    fieldContainer.innerHTML = originalHTML;
    if (savedOnClick) fieldContainer.setAttribute('onclick', savedOnClick);
    if (hadEditableClass) fieldContainer.classList.add('editable');
    fieldContainer.classList.remove('editing');
    return;
  }

  // Focus first input
  startInput.focus();

  // Cancel handler
  const cancel = () => {
    fieldContainer.innerHTML = originalHTML;
    if (savedOnClick) fieldContainer.setAttribute('onclick', savedOnClick);
    if (hadEditableClass) fieldContainer.classList.add('editable');
    fieldContainer.classList.remove('editing');
  };

  cancelBtn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    cancel();
  });

  // Save handler
  const save = async () => {
    const newStartDate = startInput.value.trim() || null;
    const newEndDate = endInput.value.trim() || null;

    // Validate: if both are set, end date should be >= start date
    if (newStartDate && newEndDate && newEndDate < newStartDate) {
      alert(t('tournament.invalidDateRange', 'End date must be on or after start date'));
      return;
    }

    saveBtn.disabled = true;
    saveBtn.textContent = t('common.saving', 'Saving...');
    cancelBtn.disabled = true;

    try {
      await updateTournamentAPI(tournamentId, {
        startDate: newStartDate,
        endDate: newEndDate
      });

      // Reload to update the display
      if (window.loadSingleTournament) {
        await window.loadSingleTournament(tournamentId);
      }
    } catch (error) {
      console.error('Failed to update dates:', error);
      alert(t('common.error', 'Error') + ': ' + (error as Error).message);
      cancel();
    }
  };

  saveBtn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    save();
  });

  // Enter key to save
  [startInput, endInput].forEach(input => {
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        save();
      } else if (e.key === 'Escape') {
        cancel();
      }
    });
  });
}

/**
 * Update tournament name (updates both the field and the header)
 */
export async function updateTournamentName(tournamentId: string, newName: string): Promise<void> {
  try {
    await updateTournamentAPI(tournamentId, { name: newName });

    // Update the tournament header title
    const titleElement = document.querySelector(`#tournament-title-${tournamentId}`) as HTMLElement;
    if (titleElement) {
      // Find the text node and update it (preserve the status badge and other elements)
      const titleText = titleElement.childNodes[1] as Text;
      if (titleText && titleText.nodeType === Node.TEXT_NODE) {
        titleText.textContent = newName;
      }
    }
  } catch (error) {
    console.error('Failed to update tournament name:', error);
    throw error;
  }
}

/**
 * Scroll to venue field and trigger edit mode
 */
export function scrollToVenueAndEdit(tournamentId: string): void {
  // Find the Key Information section and expand it if collapsed
  const keyInfoSection = document.getElementById(`keyinfo-section-${tournamentId}`);
  const keyInfoIcon = document.getElementById(`keyinfo-icon-${tournamentId}`);

  if (keyInfoSection && keyInfoSection.style.display === 'none') {
    keyInfoSection.style.display = 'block';
    if (keyInfoIcon) {
      keyInfoIcon.textContent = '▼';
    }
  }

  // Find the venue field by looking for the detail-value with venue edit onclick
  const venueField = keyInfoSection?.querySelector('.detail-value[onclick*="venue"]') as HTMLElement;

  if (venueField) {
    // Scroll the venue field into view
    venueField.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // After scrolling, trigger the click to enter edit mode
    setTimeout(() => {
      venueField.click();
    }, 300);
  }
}

/**
 * Toggle a tiebreak criterion on/off
 */
export async function toggleTiebreakCriterion(tournamentId: string, criterion: string, evt?: Event): Promise<void> {
  if (evt) {
    evt.stopPropagation();
    evt.preventDefault();
  }

  const list = document.getElementById(`tiebreak-list-${tournamentId}`);
  if (!list) return;

  // Get current enabled criteria from the DOM
  const items = list.querySelectorAll('.tiebreak-item');
  const currentCriteria: string[] = [];

  items.forEach(item => {
    const itemCriterion = item.getAttribute('data-criterion');
    const isEnabled = item.classList.contains('enabled');
    if (itemCriterion && isEnabled) {
      currentCriteria.push(itemCriterion);
    }
  });

  // Toggle the criterion
  let newCriteria: string[];
  const isCurrentlyEnabled = currentCriteria.includes(criterion);

  if (isCurrentlyEnabled) {
    // Disable - remove from list
    newCriteria = currentCriteria.filter(c => c !== criterion);
  } else {
    // Enable - add to the end of enabled criteria
    newCriteria = [...currentCriteria, criterion];
  }

  try {
    await updateTournamentAPI(tournamentId, { tiebreakCriteria: newCriteria });

    // Update UI
    const item = list.querySelector(`[data-criterion="${criterion}"]`);
    const toggle = item?.querySelector('.tiebreak-toggle');

    if (item && toggle) {
      if (isCurrentlyEnabled) {
        item.classList.remove('enabled');
        item.classList.add('disabled');
        toggle.classList.remove('active');
        toggle.textContent = '○';
      } else {
        item.classList.remove('disabled');
        item.classList.add('enabled');
        toggle.classList.add('active');
        toggle.textContent = '✓';
      }
    }
  } catch (error) {
    console.error('Failed to update tiebreak criteria:', error);
    alert(t('error.updateFailed', 'Failed to update. Please try again.'));
  }
}

/**
 * Initialize drag-and-drop for tiebreak criteria list
 */
export function initTiebreakDragDrop(tournamentId: string): void {
  const list = document.getElementById(`tiebreak-list-${tournamentId}`);
  if (!list) return;

  const items = list.querySelectorAll('.tiebreak-item');

  // Check if items already have drag listeners by checking for a marker attribute
  const firstItem = items[0];
  if (firstItem && (firstItem as any)._dragInitialized) return;

  let draggedItem: HTMLElement | null = null;

  // Create drop indicator element
  const dropIndicator = document.createElement('div');
  dropIndicator.className = 'tiebreak-drop-indicator';
  dropIndicator.style.display = 'none';

  items.forEach(item => {
    const el = item as HTMLElement;

    el.addEventListener('dragstart', (e) => {
      draggedItem = el;
      el.classList.add('dragging');
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        // Set some data to make drag work in all browsers
        e.dataTransfer.setData('text/plain', el.getAttribute('data-criterion') || '');
      }
      // Add indicator to list
      list.appendChild(dropIndicator);
    });

    el.addEventListener('dragend', async () => {
      el.classList.remove('dragging');
      dropIndicator.style.display = 'none';

      // Save new order
      if (draggedItem) {
        await saveTiebreakOrder(tournamentId);
      }
      draggedItem = null;
    });

    el.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'move';
      }

      if (draggedItem && draggedItem !== el) {
        // Determine position
        const rect = el.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        const mouseY = (e as DragEvent).clientY;

        // Show drop indicator
        dropIndicator.style.display = 'block';

        if (mouseY < midY) {
          // Insert before this element
          list.insertBefore(dropIndicator, el);
        } else {
          // Insert after this element - skip the dragged item and indicator if they're next
          let nextEl = el.nextSibling;
          while (nextEl && (nextEl === draggedItem || nextEl === dropIndicator)) {
            nextEl = nextEl.nextSibling;
          }
          list.insertBefore(dropIndicator, nextEl);
        }
      }
    });

    el.addEventListener('drop', (e) => {
      e.preventDefault();
      dropIndicator.style.display = 'none';

      if (draggedItem && draggedItem !== el) {
        // Determine position to insert
        const rect = el.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        const mouseY = (e as DragEvent).clientY;

        if (mouseY < midY) {
          list.insertBefore(draggedItem, el);
        } else {
          // Insert after this element - skip the indicator if it's next
          let nextEl = el.nextSibling;
          while (nextEl && nextEl === dropIndicator) {
            nextEl = nextEl.nextSibling;
          }
          list.insertBefore(draggedItem, nextEl);
        }
      }
    });

    // Mark this item as initialized
    (el as any)._dragInitialized = true;
  });

  // Handle dragging over the list itself (for dropping at the end)
  list.addEventListener('dragover', (e) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
  });

  // Handle drop on the list itself (catches drops between items or on the indicator)
  list.addEventListener('drop', (e) => {
    e.preventDefault();
    if (draggedItem && dropIndicator.style.display !== 'none') {
      // Insert at the indicator's position
      list.insertBefore(draggedItem, dropIndicator);
    }
    dropIndicator.style.display = 'none';
  });

  // Handle drop on the indicator itself
  dropIndicator.addEventListener('dragover', (e) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
  });

  dropIndicator.addEventListener('drop', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedItem) {
      list.insertBefore(draggedItem, dropIndicator);
    }
    dropIndicator.style.display = 'none';
  });

  list.addEventListener('dragleave', (e) => {
    // Only hide if leaving the list entirely
    if (!list.contains(e.relatedTarget as Node)) {
      dropIndicator.style.display = 'none';
    }
  });
}

/**
 * Save the current tiebreak order to the server
 */
async function saveTiebreakOrder(tournamentId: string): Promise<void> {
  const list = document.getElementById(`tiebreak-list-${tournamentId}`);
  if (!list) return;

  // Get enabled criteria in current order
  const items = list.querySelectorAll('.tiebreak-item.enabled');
  const newCriteria: string[] = [];

  items.forEach(item => {
    const criterion = item.getAttribute('data-criterion');
    if (criterion) {
      newCriteria.push(criterion);
    }
  });

  try {
    await updateTournamentAPI(tournamentId, { tiebreakCriteria: newCriteria });
  } catch (error) {
    console.error('Failed to save tiebreak order:', error);
    alert(t('error.updateFailed', 'Failed to update. Please try again.'));
  }
}

// Export to window
window.toggleTournamentDetails = toggleTournamentDetails;
window.editField = editField;
window.toggleRated = toggleRated;
window.setInitialColor = setInitialColor;
window.setTimeControl = setTimeControl;
window.setFormat = setFormat;
window.editDateRange = editDateRange;
window.updateTournamentName = updateTournamentName;
window.scrollToVenueAndEdit = scrollToVenueAndEdit;
window.toggleTiebreakCriterion = toggleTiebreakCriterion;
window.initTiebreakDragDrop = initTiebreakDragDrop;
